<i18n src="./resources/locales.json"></i18n>

<template>
  <v-dialog v-model="dialog" persistent max-width="600px">
    <v-tabs v-model="tab" class="d-none" />

    <v-tabs-items v-model="tab">
      <v-tab-item>
        <v-card color="surface lighten-1" class="main-card">
          <v-card-title primary-title>
            {{ currentDashboardName }}
            <v-spacer />
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-subtitle v-once>
            {{ $t('header.subtitle') }}
          </v-card-subtitle>

          <v-divider />
          <v-progress-linear v-show="isLoading" class="loading" height="2" color="primary" indeterminate />

          <div class="aic-config-body surface">
            <v-list subheader two-line flat color="surface" :rounded="false" over>
              <v-list-item-group class="dashboard-attributes">
                <v-list-item :ripple="false">
                  <v-text-field
                    ref="dashboardName"
                    v-model="currentDashboardName"
                    :label="$t('dashboardName')"
                    class="text-sm-left"
                    required
                    :disabled="isLoading"
                    :rules="dashboardNameRules"
                    :counter="64"
                    @keyup.enter="updateDashboardName"
                  />
                </v-list-item>
              </v-list-item-group>
              <v-subheader class="group-subheader">{{ $t('dashboardCards') }}</v-subheader>
              <v-list-item-group multiple>
                <v-list-item v-for="(card, index) in dashboardCards" ref="cards" :key="index">
                  <template>
                    <v-list-item-content>
                      <v-list-item-title>{{ getCardName(card.componentName) }}</v-list-item-title>
                      <v-list-item-subtitle v-if="card.selectedBusinessProcessId">
                        {{ $t('selectedBp') }}: {{ getBusinessProcessName(card.selectedBusinessProcessId) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click="removeCustomCard(card._id, index)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </template>
                </v-list-item>
                <v-list-item @click="tab = 1">
                  <template>
                    <v-list-item-action>
                      <v-icon left>mdi-plus</v-icon>
                    </v-list-item-action>

                    <v-list-item-content>
                      <v-list-item-title>{{ $t('addNewCard') }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </div>

          <v-divider />

          <v-card-actions>
            <v-spacer />

            <v-btn text @click="close">
              <span v-once>{{ $t('buttons.cancel') }}</span>
            </v-btn>
            <v-btn :loading="isLoading" :disabled="isLoading" color="primary" depressed @click="saveAicConfig">
              <span v-once>{{ $t('buttons.save') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
      <v-tab-item>
        <v-card color="surface lighten-1">
          <v-card-title primary-title>
            <div class="text-left">
              <div v-once class="text-h5">{{ $t('header.addCardTitle') }}</div>
              <span v-once class="grey--text">{{ $t('header.addCardSubtitle') }}</span>
            </div>
            <v-spacer />
            <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>

          <v-divider />

          <div class="aic-add-card-body pa-4">
            <div class="customization-form">
              <v-select
                v-model="newCard.componentName"
                :items="availableCards"
                item-value="componentName"
                item-text="title"
                menu-props="auto"
                :label="$t('newCard.cardType')"
              ></v-select>
              <v-select
                v-show="supportsBPSelection"
                v-model="newCard.selectedBusinessProcessId"
                :items="BUSINESS_PROCESSES_STATS"
                item-value="businessProcessId"
                item-text="businessProcessName"
                menu-props="auto"
                :label="$t('newCard.businessProcess')"
              ></v-select>
            </div>
          </div>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="tab = 0">
              <span v-once>{{ $t('buttons.discard') }}</span>
            </v-btn>
            <v-btn color="primary" :disabled="newCard.componentName === ''" depressed @click="addCard">
              <span v-once>{{ $t('buttons.create') }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

const AIC_SETTINGS_UPDATED_EVENT = 'aic-settings-updated';
const AIC_SETTINGS_CANCELLED_EVENT = 'aic-settings-cancelled';

export default {
  name: 'EcsAicConfigDialog',

  components: {},

  data() {
    return {
      dialog: false,
      availableCards: [
        // TODO bar/area chart components.
        // {
        //   title: 'Agent Metrics',
        //   componentName: 'EcsAgentMetricsCardBar',
        //   description: 'Average time spent handling an interaction represented in a Bar chart.'
        // },
        // {
        //   title: 'Agents Summary',
        //   componentName: 'EcsAgentsSummaryCard',
        //   description: 'Ready Agents, Service Level, Average Speed of Answer, Interactions Answered, etc.'
        // },
        {
          title: this.$t('queueStatusCard.title'),
          componentName: this.$t('queueStatusCard.componentName'),
          description: this.$t('queueStatusCard.description'),
          supportsBPSelection: true
        },
        {
          title: this.$t('queueSummaryCard.title'),
          componentName: this.$t('queueSummaryCard.componentName'),
          description: this.$t('queueSummaryCard.description'),
          supportsBPSelection: true
        },
        {
          title: this.$t('keyPerformanceCard.title'),
          componentName: this.$t('keyPerformanceCard.componentName'),
          description: this.$t('keyPerformanceCard.description'),
          supportsBPSelection: false
        },
        {
          title: this.$t('queueBPSummaryCard.title'),
          componentName: this.$t('queueBPSummaryCard.componentName'),
          description: this.$t('queueBPSummaryCard.description'),
          supportsBPSelection: false
        }
      ],
      currentDashboardName: '',
      newCard: {
        componentName: '',
        selectedBusinessProcessId: null,
        startBusinessProcessId: null
      },
      isLoading: false,
      tab: 0,
      dashboardNameRules: [
        value => !!value || this.$t('dashboardRules.required'),
        value => (value || '').length <= 64 || this.$t('dashboardRules.max')
      ]
    };
  },

  computed: {
    ...mapGetters('DASHBOARD', ['defaultDashboard', 'selectedDashboard']),

    ...mapGetters('SETTINGS', ['dashboardSettings']),

    ...mapGetters('STATS', ['BUSINESS_PROCESSES_STATS']),

    supportsBPSelection() {
      if (!this.newCard.componentName) {
        return false;
      }
      return this.availableCards.find(card => card.componentName === this.newCard.componentName).supportsBPSelection;
    },

    isDefaultDashboard() {
      return this.dashboardSettings.dashboards.length === 0;
    },

    dashboardCards() {
      return (this.selectedDashboard || {}).cards;
    }
  },

  methods: {
    ...mapActions('DASHBOARD', ['setSelectedDashboard']),

    ...mapActions('SETTINGS', ['updateCurrentUserDashboardSettings']),

    updateDashboardName() {
      if (this.selectedDashboard.name === this.currentDashboardName) {
        return false;
      }

      if (!this.$refs.dashboardName.validate()) {
        this.$refs.dashboardName.focus();
        return false;
      }

      const dashboards = this.dashboardSettings.dashboards;
      let updatedDashboards = null;

      if (this.isDefaultDashboard) {
        updatedDashboards = [
          {
            ...this.defaultDashboard,
            name: this.currentDashboardName
          }
        ];
      } else {
        updatedDashboards = dashboards.map(dashboard => {
          if (this.selectedDashboard._id === dashboard._id) {
            dashboard.name = this.currentDashboardName;
          }
          return dashboard;
        });
      }

      this.isLoading = true;
      return this.updateCurrentUserDashboardSettings({ dashboards: updatedDashboards })
        .then(this.refresh)
        .finally(() => {
          this.isLoading = false;
        });
    },
    addCard() {
      this.tab = 0;
      const dashboards = this.dashboardSettings.dashboards;
      let updatedDashboards = null;
      this.newCard.startBusinessProcessId = this.newCard.selectedBusinessProcessId
        ? this.newCard.selectedBusinessProcessId
        : this.BUSINESS_PROCESSES_STATS[0].businessProcessId;
      if (this.isDefaultDashboard) {
        updatedDashboards = [{ ...this.defaultDashboard }];
        updatedDashboards[0].cards.push(this.newCard);
      } else {
        updatedDashboards = dashboards.map(dashboard => {
          if (this.selectedDashboard._id === dashboard._id) {
            dashboard.cards = [...dashboard.cards, this.newCard];
          }
          return dashboard;
        });
      }
      this.isLoading = true;
      this.updateCurrentUserDashboardSettings({ dashboards: updatedDashboards })
        .then(this.refresh)
        .then(this.blinkLastItem)
        .finally(() => {
          this.isLoading = false;
          this.newCard = {
            componentName: '',
            selectedBusinessProcessId: null
          };
        });
    },
    refresh({ data }) {
      let updatedDashboard;

      if (this.selectedDashboard._id) {
        updatedDashboard = data.dashboardSettings.dashboards.find(
          dashboard => this.selectedDashboard._id === dashboard._id
        );
      } else {
        updatedDashboard = data.dashboardSettings.dashboards[0];
      }

      this.setSelectedDashboard(updatedDashboard);
    },
    blinkLastItem() {
      const cards = this.$refs.cards;
      if (cards.length === 0) {
        return;
      }
      const lastItem = cards[cards.length - 1];
      if (lastItem) {
        lastItem.$el.classList.add('blink');
        setTimeout(() => lastItem.$el.classList.remove('blink'), 1500);
      }
    },
    getBusinessProcessName(id) {
      const businessProcess = this.BUSINESS_PROCESSES_STATS.find(bp => bp.businessProcessId === id);
      return (businessProcess || {}).businessProcessName;
    },
    getCardName(componentName) {
      const card = this.availableCards.find(c => c.componentName === componentName);
      return card.title;
    },
    removeCustomCard(cardId, index) {
      const dashboards = this.dashboardSettings.dashboards;
      let updatedDashboards = null;

      if (this.isDefaultDashboard) {
        updatedDashboards = [{ ...this.defaultDashboard }];
        updatedDashboards[0].cards = updatedDashboards[0].cards.filter((card, cardIndex) => cardIndex !== index);
      } else {
        updatedDashboards = dashboards.map(dashboard => {
          if (this.selectedDashboard._id === dashboard._id) {
            dashboard.cards = dashboard.cards.filter(card => card._id !== cardId);
          }
          return dashboard;
        });
      }

      this.isLoading = true;
      this.updateCurrentUserDashboardSettings({ dashboards: updatedDashboards })
        .then(this.refresh)
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.dialog = false;
      this.currentDashboardName = this.selectedDashboard.name;
      this.$off(AIC_SETTINGS_UPDATED_EVENT);
      this.$emit(AIC_SETTINGS_CANCELLED_EVENT);
    },
    open() {
      this.dialog = true;
      this.editDashboardName = false;
      this.currentDashboardName = this.selectedDashboard.name;
      return new Promise((resolve, reject) => {
        this.$once(AIC_SETTINGS_UPDATED_EVENT, resolve);
        this.$once(AIC_SETTINGS_CANCELLED_EVENT, reject);
      });
    },
    async saveAicConfig() {
      if (this.selectedDashboard.name !== this.currentDashboardName) {
        const successfullyUpdated = await this.updateDashboardName();
        if (successfullyUpdated === false) {
          return;
        }
      }
      this.$emit(AIC_SETTINGS_UPDATED_EVENT);
      this.$off(AIC_SETTINGS_CANCELLED_EVENT);
      this.close();
    }
  }
};
</script>

<style scoped>
.main-card {
  border-radius: 0 !important;
}
.aic-config-body,
.aic-add-card-body {
  min-height: 400px;
}
.v-list-item:hover {
  background-color: var(--v-hover-background);
}
.form-group {
  padding: 8px 0 20px 32px;
}
.form-group-title {
  height: 36px;
  font-size: 12px;
}
.group-subheader {
  text-transform: uppercase;
  color: var(--v-primary-base);
  margin: 20px 0 -8px 0;
}
.dashboard-attributes {
  padding-top: 20px;
}
.loading {
  position: absolute;
}
</style>
